import { type FC, type ReactNode, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuth } from '@/shared/auth';
import type { Maybe } from '@/shared/libs';
import { Button, LoaderOverlay, PageHeader } from '@/shared/ui';
import { resolveUserRole, UserRoleEnum } from '@entities/User';
import type { Offer } from '@psp-aggregator/sdk';
import { AgentAnalytics } from '@widgets/AgentAnalytics';
import { AppShell, markCatalogSeen, useNavBadges } from '@widgets/AppShell';
import { DealTicket } from '@widgets/DealTicket';
import { MyDeals } from '@widgets/MyDeals';
import { OfferCatalog } from '@widgets/OfferCatalog';
import { ProposalBuilder } from '@widgets/ProposalBuilder';

import { AGENT_TABS, AgentTab, isAgentTab } from '../../model/agent-tabs';

const AGENT_HEADING_KEY: Record<
  AgentTab,
  | 'pages.agent.headings.analytics'
  | 'pages.agent.headings.catalog'
  | 'pages.agent.headings.deals'
> = {
  [AgentTab.Analytics]: 'pages.agent.headings.analytics',
  [AgentTab.Catalog]: 'pages.agent.headings.catalog',
  [AgentTab.Deals]: 'pages.agent.headings.deals',
};

const AGENT_SUBTITLE_KEY: Record<
  AgentTab,
  | 'pages.agent.subtitles.analytics'
  | 'pages.agent.subtitles.catalog'
  | 'pages.agent.subtitles.deals'
> = {
  [AgentTab.Analytics]: 'pages.agent.subtitles.analytics',
  [AgentTab.Catalog]: 'pages.agent.subtitles.catalog',
  [AgentTab.Deals]: 'pages.agent.subtitles.deals',
};

export interface AgentDashboardViewProps {
  activeTab: AgentTab;
  onTabChange: (tab: AgentTab) => void;
}

export const AgentDashboardView: FC<AgentDashboardViewProps> = ({
  activeTab,
  onTabChange,
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [selectedOffers, setSelectedOffers] = useState<Offer[]>([]);
  const [proposalOpen, setProposalOpen] = useState(false);
  const [selectedDealId, setSelectedDealId] = useState<Maybe<string>>(null);
  const role = user ? resolveUserRole(user.role) : undefined;
  const badges = useNavBadges(role);

  useEffect(() => {
    if (activeTab === AgentTab.Catalog) {
      markCatalogSeen(role ?? UserRoleEnum.Agent);
    }
  }, [activeTab, role]);

  const tabs = useMemo(
    () =>
      AGENT_TABS.map((tab) => ({
        badge:
          tab.key === AgentTab.Deals
            ? badges.deals || undefined
            : tab.key === AgentTab.Catalog
              ? badges.catalog || undefined
              : undefined,
        badgeTone: (tab.key === AgentTab.Deals ? 'rose' : 'amber') as
          'rose' | 'amber',
        icon: tab.icon,
        key: tab.key,
        label: t(tab.labelKey),
      })),
    [badges.catalog, badges.deals, t],
  );

  const handleTabChange = (tab: string): void => {
    if (isAgentTab(tab)) {
      onTabChange(tab);
    }
  };

  if (!user) {
    return <LoaderOverlay show />;
  }

  const tabKey = isAgentTab(activeTab) ? activeTab : AgentTab.Analytics;
  const content: Record<AgentTab, ReactNode> = {
    [AgentTab.Analytics]: <AgentAnalytics />,
    [AgentTab.Catalog]: (
      <OfferCatalog
        selectedIds={selectedOffers.map((offer) => offer.id)}
        onSelectionChange={setSelectedOffers}
      />
    ),
    [AgentTab.Deals]: <MyDeals onOpenDeal={setSelectedDealId} />,
  };

  return (
    <AppShell activeTab={activeTab} tabs={tabs} onTabChange={handleTabChange}>
      <div>
        <PageHeader
          action={
            tabKey === AgentTab.Catalog ? (
              <Button
                disabled={selectedOffers.length === 0}
                size="sm"
                type="button"
                variant="brand"
                onClick={() => setProposalOpen(true)}>
                {t('pages.agent.catalog.buildProposal')} (
                {selectedOffers.length})
              </Button>
            ) : undefined
          }
          subtitle={t(AGENT_SUBTITLE_KEY[tabKey])}
          title={t(AGENT_HEADING_KEY[tabKey])}
        />

        <div className="mt-6">{content[tabKey]}</div>
      </div>
      {proposalOpen && (
        <ProposalBuilder
          offers={selectedOffers}
          onClose={() => setProposalOpen(false)}
          onCreated={() => setSelectedOffers([])}
        />
      )}
      {!!selectedDealId && (
        <DealTicket
          dealId={selectedDealId}
          onClose={() => setSelectedDealId(null)}
        />
      )}
    </AppShell>
  );
};

AgentDashboardView.displayName = 'AgentDashboardView';
